<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useRoute } from 'vue-router';
import {
  fetchMarketplaceJson,
  postMarketplaceJsonBody,
} from 'dashboard/helpers/marketplaceApi';

// Sipariş Düşümü (Faz 4 — önce onaylı mod): Shopify siparişi → kullanıcı
// onayıyla Ticimax'e adet satışı. Hem Entegrasyonlar sayfasında hem bağımsız
// "Siparişler" menüsünde kullanılır. 60 sn'de bir sessiz tazelenir (webhook
// anında kuyruğa yazar, açık sayfa kendiliğinden günceller).
const props = defineProps({
  channelId: { type: Number, required: true },
});

const route = useRoute();
const accountId = route.params.accountId;

const dedLoading = ref(false);
const dedSyncBusy = ref(false);
const dedRows = ref([]);
const dedCounts = ref({});
const dedUsdTry = ref(null);
const dedBusyIds = ref(new Set());
const dedExpandedId = ref(null);
const statusFilter = ref('');
const error = ref('');
const toast = ref('');
let pollTimer = null;

const DED_STATUS = {
  pending: { label: 'Onay Bekliyor', cls: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300' },
  unmatched: { label: 'Eşleşme Yok', cls: 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400' },
  processed: { label: 'Ticimax’e İşlendi', cls: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' },
  skipped: { label: 'Atlandı', cls: 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400' },
  error: { label: 'Hata', cls: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' },
};

const FILTERS = [
  { value: '', label: 'Tümü' },
  { value: 'pending', label: 'Onay Bekleyen' },
  { value: 'processed', label: 'İşlenen' },
  { value: 'unmatched', label: 'Eşleşmeyen' },
  { value: 'skipped', label: 'Atlanan' },
];

const dedPendingCount = computed(
  () => (dedCounts.value.pending || 0) + (dedCounts.value.error || 0)
);

const visibleRows = computed(() => {
  if (!statusFilter.value) return dedRows.value;
  if (statusFilter.value === 'pending')
    return dedRows.value.filter(r => r.status === 'pending' || r.status === 'error');
  return dedRows.value.filter(r => r.status === statusFilter.value);
});

function dedSetBusy(id, v) {
  const s = new Set(dedBusyIds.value);
  if (v) s.add(id);
  else s.delete(id);
  dedBusyIds.value = s;
}

function fmtDedDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return (
    d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' }) +
    ' ' +
    d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })
  );
}

function dedTryEstimate(d) {
  if (!dedUsdTry.value || !d.meta?.totalUsd) return null;
  return Math.round(d.meta.totalUsd * dedUsdTry.value);
}

async function loadDeductions(silent = false) {
  if (!silent) dedLoading.value = true;
  try {
    const data = await fetchMarketplaceJson(
      accountId,
      `channels/${props.channelId}/deductions`
    );
    dedRows.value = data.rows || [];
    dedCounts.value = data.counts || {};
    dedUsdTry.value = data.usdTry || null;
  } catch (e) {
    if (!silent) error.value = 'Sipariş listesi yüklenemedi: ' + e.message;
  } finally {
    dedLoading.value = false;
  }
}

async function syncOrdersNow() {
  dedSyncBusy.value = true;
  try {
    await postMarketplaceJsonBody(
      accountId,
      `channels/${props.channelId}/sync_orders`,
      {}
    );
    await loadDeductions();
    toast.value = 'Shopify siparişleri çekildi.';
  } catch (e) {
    error.value = 'Sipariş çekme hatası: ' + e.message;
  } finally {
    dedSyncBusy.value = false;
  }
}

async function processDeduction(d) {
  const tryTxt = dedTryEstimate(d)
    ? ` (~${dedTryEstimate(d).toLocaleString('tr-TR')} TL)`
    : '';
  // eslint-disable-next-line no-alert
  if (!window.confirm(`${d.orderNumber} siparişi Ticimax'e adet satışı olarak yazılacak${tryTxt} — stok düşecek. Onaylıyor musun?`)) return;
  dedSetBusy(d.id, true);
  try {
    const res = await postMarketplaceJsonBody(
      accountId,
      `channels/${props.channelId}/process_deduction`,
      { deductionId: d.id }
    );
    const idx = dedRows.value.findIndex(r => r.id === d.id);
    if (idx >= 0) dedRows.value[idx] = res.deduction;
    toast.value = `${d.orderNumber} → Ticimax ${res.deduction.ticimaxOrderNo} olarak işlendi.`;
    loadDeductions(true);
  } catch (e) {
    error.value = `${d.orderNumber} işlenemedi: ` + e.message;
    loadDeductions(true);
  } finally {
    dedSetBusy(d.id, false);
  }
}

async function skipDeduction(d) {
  dedSetBusy(d.id, true);
  try {
    const res = await postMarketplaceJsonBody(
      accountId,
      `channels/${props.channelId}/skip_deduction`,
      { deductionId: d.id }
    );
    const idx = dedRows.value.findIndex(r => r.id === d.id);
    if (idx >= 0) dedRows.value[idx] = res.deduction;
  } catch (e) {
    error.value = 'Atlanamadı: ' + e.message;
  } finally {
    dedSetBusy(d.id, false);
  }
}

onMounted(() => {
  loadDeductions();
  pollTimer = setInterval(() => loadDeductions(true), 60000);
});
onBeforeUnmount(() => clearInterval(pollTimer));
</script>

<template>
  <div class="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
    <div class="flex flex-wrap items-center gap-3 px-5 py-3.5 border-b border-slate-100 dark:border-slate-800">
      <h2 class="text-sm font-semibold text-slate-800 dark:text-slate-200 flex items-center gap-2">
        <span class="grid size-6 place-items-center rounded-md bg-woot-50 dark:bg-woot-900/40 text-woot-600 dark:text-woot-300 text-xs">📦</span>
        Sipariş Düşümü
        <span v-if="dedPendingCount" class="rounded-full bg-yellow-100 dark:bg-yellow-900/40 px-2 py-0.5 text-[10.5px] font-bold text-yellow-700 dark:text-yellow-300">{{ dedPendingCount }} bekliyor</span>
      </h2>
      <p class="text-[11.5px] text-slate-400 hidden md:block">
        Shopify siparişi → onayınla Ticimax’e adet satışı (stok düşümü).
      </p>
      <span v-if="dedUsdTry" class="ml-auto text-[10.5px] font-semibold text-slate-400">TCMB $ {{ dedUsdTry.toFixed(2) }}</span>
      <button
        class="flex items-center gap-1.5 rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-1.5 text-xs font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50"
        :class="{ 'ml-auto': !dedUsdTry }"
        :disabled="dedSyncBusy"
        @click="syncOrdersNow"
      >
        <span :class="dedSyncBusy ? 'i-lucide-loader-2 animate-spin' : 'i-lucide-download'" class="size-3.5" />
        Siparişleri Çek
      </button>
    </div>

    <div
      v-if="error"
      class="mx-5 mt-3 flex items-center gap-3 rounded-xl bg-red-50 dark:bg-red-950 p-3 text-sm text-red-600 dark:text-red-400"
    >
      <span class="i-lucide-alert-circle size-4 shrink-0" />
      <span class="flex-1">{{ error }}</span>
      <button class="text-xs font-semibold" @click="error = ''">✕</button>
    </div>
    <div
      v-if="toast"
      class="mx-5 mt-3 flex items-center gap-3 rounded-xl bg-green-50 dark:bg-green-900/20 p-3 text-sm text-green-700 dark:text-green-300"
    >
      <span class="i-lucide-check-circle size-4 shrink-0" />
      <span class="flex-1">{{ toast }}</span>
      <button class="text-xs font-semibold" @click="toast = ''">✕</button>
    </div>

    <!-- Durum filtresi -->
    <div class="flex flex-wrap items-center gap-1.5 px-5 py-2.5">
      <button
        v-for="f in FILTERS"
        :key="f.value"
        class="rounded-full px-2.5 py-1 text-[11px] font-semibold border"
        :class="statusFilter === f.value
          ? 'bg-woot-500 border-woot-500 text-white'
          : 'border-slate-200 dark:border-slate-700 text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800'"
        @click="statusFilter = f.value"
      >
        {{ f.label }}
        <span v-if="f.value === 'pending' && dedPendingCount" class="font-mono">{{ dedPendingCount }}</span>
      </button>
      <span class="ml-auto text-[10.5px] text-slate-400">Saatlik otomatik + sipariş anında webhook · sayfa 60 sn’de bir tazelenir</span>
    </div>

    <div v-if="dedLoading" class="flex h-24 items-center justify-center gap-2 text-slate-400">
      <span class="i-lucide-loader-2 size-4 animate-spin" />
      <span class="text-xs">Siparişler yükleniyor…</span>
    </div>
    <div v-else-if="!visibleRows.length" class="px-5 py-8 text-center text-xs text-slate-400">
      {{ statusFilter ? 'Bu durumda sipariş yok.' : 'Henüz sipariş yok. Shopify’a sipariş düştüğü anda burada belirir; “Siparişleri Çek” ile elle de alabilirsin.' }}
    </div>
    <div v-else class="overflow-x-auto">
      <table class="w-full text-[12px]">
        <thead>
          <tr class="border-b border-slate-100 dark:border-slate-800 text-left text-[10px] uppercase text-slate-400">
            <th class="px-5 py-2">Sipariş</th>
            <th class="px-3 py-2">Tarih</th>
            <th class="px-3 py-2">Müşteri</th>
            <th class="px-3 py-2">Kalemler</th>
            <th class="px-3 py-2 text-right">Tutar</th>
            <th class="px-3 py-2">Durum</th>
            <th class="px-5 py-2 text-right">İşlem</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="d in visibleRows" :key="d.id">
            <tr
              class="border-b border-slate-50 dark:border-slate-800/60 hover:bg-slate-50/60 dark:hover:bg-slate-800/40 cursor-pointer"
              @click="dedExpandedId = dedExpandedId === d.id ? null : d.id"
            >
              <td class="px-5 py-2.5 font-mono font-semibold text-slate-700 dark:text-slate-200">{{ d.orderNumber }}</td>
              <td class="px-3 py-2.5 text-slate-500 whitespace-nowrap">{{ fmtDedDate(d.orderDate) }}</td>
              <td class="px-3 py-2.5 text-slate-600 dark:text-slate-300 max-w-[160px] truncate">{{ d.customer || '—' }}</td>
              <td class="px-3 py-2.5 whitespace-nowrap">
                <span class="font-semibold text-green-600">{{ d.meta?.matchedLines ?? 0 }} eşleşen</span>
                <span v-if="d.meta?.unmatchedLines" class="text-red-500"> · {{ d.meta.unmatchedLines }} eşleşmeyen</span>
              </td>
              <td class="px-3 py-2.5 text-right font-mono whitespace-nowrap">
                ${{ (d.meta?.totalUsd ?? 0).toLocaleString('en-US') }}
                <span v-if="dedTryEstimate(d)" class="text-[10px] text-slate-400 block">≈{{ dedTryEstimate(d).toLocaleString('tr-TR') }} TL</span>
              </td>
              <td class="px-3 py-2.5">
                <span class="rounded-full px-2 py-0.5 text-[10px] font-bold" :class="(DED_STATUS[d.status] || DED_STATUS.pending).cls">
                  {{ (DED_STATUS[d.status] || DED_STATUS.pending).label }}
                </span>
                <span v-if="d.ticimaxOrderNo" class="block mt-0.5 font-mono text-[10px] text-slate-400">{{ d.ticimaxOrderNo }}</span>
              </td>
              <td class="px-5 py-2.5 text-right whitespace-nowrap" @click.stop>
                <template v-if="d.status === 'pending' || d.status === 'error'">
                  <button
                    class="rounded-lg bg-woot-500 px-2.5 py-1 text-[11px] font-bold text-white hover:bg-woot-600 disabled:opacity-50"
                    :disabled="dedBusyIds.has(d.id)"
                    @click="processDeduction(d)"
                  >
                    <span v-if="dedBusyIds.has(d.id)" class="i-lucide-loader-2 size-3 animate-spin inline-block" />
                    Ticimax’e İşle
                  </button>
                  <button
                    class="ml-1.5 rounded-lg border border-slate-200 dark:border-slate-700 px-2 py-1 text-[11px] font-semibold text-slate-400 hover:text-slate-600 disabled:opacity-50"
                    :disabled="dedBusyIds.has(d.id)"
                    @click="skipDeduction(d)"
                  >
                    Atla
                  </button>
                </template>
              </td>
            </tr>
            <tr v-if="dedExpandedId === d.id" class="border-b border-slate-100 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-800/30">
              <td colspan="7" class="px-5 py-3">
                <div v-if="d.lastError" class="mb-2 rounded-lg bg-red-50 dark:bg-red-950 px-3 py-2 text-[11px] text-red-600 dark:text-red-400">{{ d.lastError }}</div>
                <div class="grid gap-1">
                  <div
                    v-for="(l, li) in d.lines"
                    :key="li"
                    class="flex flex-wrap items-center gap-2 rounded-lg bg-white dark:bg-slate-900 px-3 py-1.5 border border-slate-100 dark:border-slate-800"
                  >
                    <span :class="l.matched ? 'i-lucide-check-circle text-green-500' : 'i-lucide-alert-circle text-red-400'" class="size-3.5 shrink-0" />
                    <span class="font-mono text-[11px] text-slate-500">{{ l.sku || 'SKU yok' }}</span>
                    <span class="text-[11.5px] text-slate-700 dark:text-slate-200 font-semibold">{{ l.color }} / {{ l.size }}</span>
                    <span class="text-[11px] text-slate-400 truncate max-w-[240px]">{{ l.title }}</span>
                    <span class="ml-auto font-mono text-[11px]">×{{ l.qty }} · ${{ l.unitPrice }}</span>
                    <span v-if="!l.matched" class="rounded bg-red-50 dark:bg-red-950 px-1.5 py-0.5 text-[9.5px] font-bold text-red-500">Ticimax’e yazılMAZ — ÜM eşleşmesi yok</span>
                  </div>
                </div>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>
  </div>
</template>
